import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { fmtMoney } from "@/lib/format";
import { CURRENCY_SYMBOL } from "@/lib/format";
import StatCard from "@/components/StatCard";
import { Loading, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, Plus, Trash2, Repeat, CalendarClock } from "lucide-react";

const FREQS = [
  { id: "one-off", label: "One-off" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
  { id: "annual", label: "Annual" },
];
const CURRENCIES = ["USD", "GBP", "EUR", "CAD", "MXN", "SEK"];
const blank = { name: "", amount: "", currency: "USD", frequency: "monthly", date: "", note: "" };
const freqBadge = { "one-off": "#8B5CF6", weekly: "#F59E0B", monthly: "#007AFF", annual: "#10B981" };
const monthlyEquiv = { "one-off": 0, weekly: 52 / 12, monthly: 1, annual: 1 / 12 };

export default function OtherCostsTab() {
  const { currency, toDisplay } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/other-costs").then((r) => { setRows(r.data.data); setLoading(false); })
      .catch(() => { toast.error("Could not load costs."); setLoading(false); });
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    try {
      await api.post("/other-costs", { ...form, amount: Number(form.amount) || 0 });
      toast.success("Cost added");
      setOpen(false); setForm(blank); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/other-costs/${id}`); load(); } catch { toast.error("Could not delete."); } };

  if (loading) return <Loading />;

  const monthlyEst = rows.filter((c) => c.frequency !== "one-off").reduce((a, c) => a + toDisplay(Number(c.amount) || 0, c.currency) * monthlyEquiv[c.frequency], 0);
  const oneOffTotal = rows.filter((c) => c.frequency === "one-off").reduce((a, c) => a + toDisplay(Number(c.amount) || 0, c.currency), 0);

  return (
    <div className="space-y-6" data-testid="other-costs-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-[#A3A3A3] max-w-lg">Track business overheads — subscriptions, software, prep, storage, salaries. Recurring costs are spread across the period and deducted from your Cost & Profit net figure.</p>
        <Button data-testid="add-cost-button" onClick={() => { setForm(blank); setOpen(true); }} className="h-9 bg-[#007AFF] hover:bg-[#005BB5] rounded-full px-4 font-semibold">
          <Plus className="h-4 w-4 mr-1" /> Add Cost
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard testid="oc-count" label="Cost Items" value={String(rows.length)} sub="tracked" icon={Wallet} accent="#007AFF" />
        <StatCard testid="oc-monthly" label="Recurring / month" value={fmtMoney(monthlyEst, currency)} sub={`monthly-equivalent · ${currency}`} icon={Repeat} accent="#F59E0B" />
        <StatCard testid="oc-oneoff" label="One-off Total" value={fmtMoney(oneOffTotal, currency)} sub={`non-recurring · ${currency}`} icon={CalendarClock} accent="#8B5CF6" />
      </div>

      {!rows.length ? (
        <EmptyState title="No other costs yet" message="Add your first overhead — e.g. a monthly software subscription, weekly prep fee, or a one-off purchase. It'll be factored into your profit." />
      ) : (
        <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden" data-testid="other-costs-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#666] text-xs uppercase tracking-wider">
                <th className="px-5 py-3">Name</th><th className="px-5 py-3">Frequency</th><th className="px-5 py-3">Date</th>
                <th className="px-5 py-3 text-right">Amount</th><th className="px-5 py-3">Note</th><th className="px-5 py-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} data-testid={`cost-row-${c.id}`} className="border-t border-[#141414] hover:bg-white/5 transition-colors duration-150">
                    <td className="px-5 py-3 text-white font-medium">{c.name}</td>
                    <td className="px-5 py-3"><span className="px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: `${freqBadge[c.frequency]}22`, color: freqBadge[c.frequency] }}>{c.frequency}</span></td>
                    <td className="px-5 py-3 text-[#A3A3A3] tnum text-xs">{c.date}</td>
                    <td className="px-5 py-3 text-right tnum text-white">
                      {CURRENCY_SYMBOL[c.currency] || ""}{Number(c.amount).toFixed(2)} {c.currency}
                      {c.currency !== currency && <span className="block text-[11px] text-[#666]" data-testid={`cost-conv-${c.id}`}>≈ {fmtMoney(toDisplay(Number(c.amount) || 0, c.currency), currency)}</span>}
                    </td>
                    <td className="px-5 py-3 text-[#666] max-w-[220px] truncate" title={c.note}>{c.note || "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <button data-testid={`delete-cost-${c.id}`} onClick={() => remove(c.id)} className="inline-flex items-center gap-1 text-[#A3A3A3] hover:text-[#EF4444] text-xs font-semibold transition-colors duration-150"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0d0d0d] border-[#262626] text-white" data-testid="cost-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><Wallet className="h-5 w-5 text-[#007AFF]" /> Add Other Cost</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">Recurring costs are spread across the period in Cost & Profit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Name</Label>
              <Input data-testid="cost-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Helium10 subscription" className="bg-[#111] border-[#262626]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Amount</Label>
                <Input data-testid="cost-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-[#111] border-[#262626]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Currency</Label>
                <select data-testid="cost-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="w-full h-9 bg-[#111] border border-[#262626] rounded-md px-2 text-sm text-white">
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Frequency</Label>
                <select data-testid="cost-frequency" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="w-full h-9 bg-[#111] border border-[#262626] rounded-md px-2 text-sm text-white">
                  {FREQS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">{form.frequency === "one-off" ? "Date" : "Start date"}</Label>
                <Input data-testid="cost-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[#111] border-[#262626]" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-[#A3A3A3]">Note</Label>
              <Input data-testid="cost-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-[#111] border-[#262626]" />
            </div>
          </div>
          <DialogFooter><Button data-testid="cost-save" onClick={save} className="bg-[#10B981] hover:bg-[#0d9668] text-black">Add Cost</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
